import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { FlowNode, FlowEdge } from './workflowStore';
import { workflowApi } from '../services/api';
import type { NodeDSL, EdgeDSL, WorkflowVisibility } from '../types';

export interface SavedWorkflow {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'inactive';
  visibility?: WorkflowVisibility;
  teamId?: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  uploadedFile: {
    id: string;
    name: string;
    rowCount: number;
    columns: string[];
  } | null;
  nodeCount: number;
  executionCount: number;
  requiredFields?: string[];
  lastExecutedAt?: string;
  createdAt: string;
  updatedAt: string;
  canEdit?: boolean; // 当前用户是否有编辑权限
  changeSummary?: string; // 修改说明（仅在更新时使用）
}

/** API 返回的工作流项（与后端 StoredWorkflow 对应） */
interface ApiWorkflowItem {
  id: string;
  name: string;
  description?: string;
  status?: string;
  visibility?: WorkflowVisibility;
  teamId?: string;
  nodes?: Array<{ id: string; type: string; config?: Record<string, unknown>; position?: { x: number; y: number } }>;
  edges?: Array<{ from: string; to: string }>;
  nodeCount?: number;
  executionCount?: number;
  requiredFields?: string[];
  createdAt?: string;
  updatedAt?: string;
  canEdit?: boolean;
}

function apiWorkflowToSaved(w: ApiWorkflowItem): SavedWorkflow {
  const nodes: FlowNode[] = (w.nodes ?? []).map((n) => ({
    id: n.id,
    type: n.type,
    position: n.position ?? { x: 0, y: 0 },
    data: {
      label: (n.config?.label as string) ?? n.type,
      config: (n.config?.params ? { type: n.type, params: n.config.params } : { type: n.type, params: n.config ?? {} }) as FlowNode['data']['config'],
      status: 'idle',
    },
  }));
  const edges: FlowEdge[] = (w.edges ?? []).map((e, i) => ({
    id: `e_${e.from}_${e.to}_${i}`,
    source: e.from,
    target: e.to,
  }));
  const createdAt = typeof w.createdAt === 'string' ? w.createdAt : new Date().toISOString();
  const updatedAt = typeof w.updatedAt === 'string' ? w.updatedAt : new Date().toISOString();
  return {
    id: w.id,
    name: w.name,
    description: w.description ?? '',
    status: (w.status as SavedWorkflow['status']) ?? 'draft',
    visibility: w.visibility,
    teamId: w.teamId,
    nodes,
    edges,
    uploadedFile: null,
    nodeCount: w.nodeCount ?? nodes.length,
    executionCount: w.executionCount ?? 0,
    requiredFields: w.requiredFields,
    createdAt,
    updatedAt,
    canEdit: w.canEdit ?? true, // 默认可以编辑（兼容旧数据）
  };
}

function savedToRequest(wf: SavedWorkflow): { name: string; description?: string; visibility?: WorkflowVisibility; nodes: NodeDSL[]; edges: EdgeDSL[] } {
  const nodes: NodeDSL[] = wf.nodes.map((n) => ({
    id: n.id,
    type: n.type,
    config: (n.data?.config ?? {}) as unknown as Record<string, unknown>,
    position: n.position,
  }));
  const edges: EdgeDSL[] = wf.edges.map((e) => ({ from: e.source, to: e.target }));
  return {
    name: wf.name,
    description: wf.description || undefined,
    visibility: wf.visibility,
    nodes,
    edges,
  };
}

interface WorkflowListState {
  workflows: SavedWorkflow[];
  /** 从服务端拉取列表并写入 store */
  fetchWorkflows: () => Promise<void>;
  /** 创建并持久化到服务端，成功后写入 store，返回服务端创建的 workflow（含 id） */
  addWorkflow: (wf: SavedWorkflow) => Promise<SavedWorkflow>;
  /** 更新并持久化到服务端，成功后更新 store */
  updateWorkflow: (id: string, updates: Partial<SavedWorkflow>) => Promise<void>;
  /** 仅更新 store 中的工作流（不请求 API），用于恢复版本后同步本地状态，避免误触保存产生新版本 */
  updateWorkflowLocal: (id: string, updates: Partial<SavedWorkflow>) => void;
  /** 从服务端删除，成功后从 store 移除 */
  deleteWorkflow: (id: string) => Promise<void>;
  getWorkflow: (id: string) => SavedWorkflow | undefined;
  incrementExecutionCount: (id: string) => void;
}

export const useWorkflowListStore = create<WorkflowListState>()(
  persist(
    (set, get) => ({
      workflows: [],

      fetchWorkflows: async () => {
        try {
          const res = await workflowApi.listWorkflows();
          // API 返回的是 ApiResponse<{ workflows: []; total: number }>
          const data = (res as unknown as { data?: { workflows?: ApiWorkflowItem[]; total?: number } }).data;
          const list = data?.workflows ?? [];
          set({
            workflows: list.map(apiWorkflowToSaved),
          });
        } catch (e) {
          console.error('fetchWorkflows failed:', e);
          throw e;
        }
      },

      addWorkflow: async (wf) => {
        const req = savedToRequest(wf);
        const res = await workflowApi.createWorkflow({ ...req, status: wf.status });
        const body = res as unknown as { data?: ApiWorkflowItem };
        const created = body?.data;
        if (!created) throw new Error('Create workflow returned no data');
        const saved = apiWorkflowToSaved(created);
        set((state) => ({ workflows: [saved, ...state.workflows] }));
        return saved;
      },

      updateWorkflow: async (id, updates) => {
        const current = get().workflows.find((w) => w.id === id);
        if (!current) throw new Error('Workflow not found');
        const merged: SavedWorkflow = {
          ...current,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        const req = savedToRequest(merged);
        // 提取 changeSummary 并传递给 API
        const changeSummary = (updates as { changeSummary?: string }).changeSummary;
        await workflowApi.updateWorkflow(id, { 
          ...req, 
          status: merged.status,
          changeSummary,
        } as Parameters<typeof workflowApi.updateWorkflow>[1]);
        set((state) => ({
          workflows: state.workflows.map((w) =>
            w.id === id ? { ...w, ...updates, updatedAt: new Date().toISOString() } : w
          ),
        }));
      },

      updateWorkflowLocal: (id, updates) => {
        set((state) => ({
          workflows: state.workflows.map((w) =>
            w.id === id ? { ...w, ...updates, updatedAt: (updates.updatedAt ?? new Date().toISOString()) } : w
          ),
        }));
      },

      deleteWorkflow: async (id) => {
        await workflowApi.deleteWorkflow(id);
        set((state) => ({
          workflows: state.workflows.filter((w) => w.id !== id),
        }));
      },

      getWorkflow: (id) => get().workflows.find((w) => w.id === id),

      incrementExecutionCount: (id) =>
        set((state) => ({
          workflows: state.workflows.map((w) =>
            w.id === id
              ? {
                  ...w,
                  executionCount: w.executionCount + 1,
                  lastExecutedAt: new Date().toISOString(),
                }
              : w
          ),
        })),
    }),
    {
      name: 'workflow-list-storage',
    }
  )
);
